import React, { useState, useMemo, useCallback } from 'react';
import { ScheduleRange } from 'store/scheduleRanges/interfaces';
import { endOfDay, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import { millisecondsInSecond } from 'helpers/timeConsts';
import checkIsDateMidnight from 'helpers/checkIsDateMidnight';
import { ScheduleCalendarContext } from './ScheduleCalendar.context';
import { ScheduleWorkingHours } from '../interfaces';

export interface ScheduleCalendarContextProps {
  scheduleRanges: ScheduleRange[];
  children: React.ReactNode;
}

export const draftScheduleWorkingHoursId = -1;

export default function ScheduleCalendarProvider({
  scheduleRanges,
  children,
}: ScheduleCalendarContextProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draftScheduleWorkingHours, setDraftScheduleWorkingHours] =
    useState<ScheduleWorkingHours | null>(null);
  // state for feature draftScheduleWorkingHours on adding resize
  const [isDraftCreating, setIsDraftCreating] = useState(false);
  const [draftAnchorEl, setDraftAnchorEl] = useState<HTMLDivElement | null>(
    null,
  );

  const handleAddDraftScheduleWorkingHours = useCallback(
    (scheduleWorkingHours: ScheduleWorkingHours) => {
      if (draftScheduleWorkingHours) {
        return;
      }

      setDraftScheduleWorkingHours(scheduleWorkingHours);
    },
    [draftScheduleWorkingHours],
  );

  const updateStartDateOfDraftScheduleWorkingHours = useCallback(
    (newStartDate: Date) => {
      setDraftScheduleWorkingHours((prev) => {
        if (!prev) {
          return null;
        }

        const isStartDateSameDay = isSameDay(newStartDate, prev.endDate);

        if (!isStartDateSameDay) {
          return prev;
        }
        let { endDate } = prev;
        const isEndDateBeforeStartDate = !isBefore(newStartDate, endDate);

        if (isEndDateBeforeStartDate) {
          endDate = startOfDay(newStartDate);
          endDate.setHours(
            newStartDate.getHours() + 1,
            newStartDate.getMinutes(),
            0,
            0,
          );
        }

        const seconds =
          (new Date(endDate).getTime() - new Date(newStartDate).getTime()) /
          millisecondsInSecond;
        return {
          ...prev,
          startDate: newStartDate,
          endDate,
          seconds,
        };
      });
    },
    [],
  );
  const updateEndDateOfDraftScheduleWorkingHours = useCallback(
    (newEndDate: Date, ignoreLimit?: boolean) => {
      setDraftScheduleWorkingHours((prev) => {
        if (!prev) {
          return null;
        }
        const isEndDateSameDay = isSameDay(newEndDate, prev.startDate);
        const isEndDateAfterStartDate = isAfter(newEndDate, prev.startDate);

        if (!isEndDateSameDay || (!isEndDateAfterStartDate && !ignoreLimit)) {
          return prev;
        }

        const isEndDateMidnight = checkIsDateMidnight(newEndDate);

        const endDateInMsToCompare = isEndDateMidnight
          ? newEndDate.getTime() + 1
          : newEndDate.getTime();

        const seconds =
          Math.ceil((endDateInMsToCompare - new Date(prev.startDate).getTime()) /
          millisecondsInSecond);

        return {
          ...prev,
          endDate: isEndDateMidnight ? endOfDay(newEndDate) : newEndDate,
          seconds,
        };
      });
    },
    [],
  );

  const handleOnCloseDraftForm = () => {
    setDraftAnchorEl(null);
    setDraftScheduleWorkingHours(null);
  };

  const contextValue = useMemo(
    () => ({
      scheduleRanges,
      currentDate,
      setCurrentDate,
      draftScheduleWorkingHours,
      handleAddDraftScheduleWorkingHours,
      isDraftCreating,
      setIsDraftCreating,
      updateEndDateOfDraftScheduleWorkingHours,
      updateStartDateOfDraftScheduleWorkingHours,
      handleOnCloseDraftForm,
      draftAnchorEl,
      setDraftAnchorEl,
    }),
    [
      scheduleRanges,
      currentDate,
      draftScheduleWorkingHours,
      handleAddDraftScheduleWorkingHours,
      isDraftCreating,
      updateEndDateOfDraftScheduleWorkingHours,
      updateStartDateOfDraftScheduleWorkingHours,
      draftAnchorEl,
    ],
  );

  return (
    <ScheduleCalendarContext.Provider value={contextValue}>
      {children}
    </ScheduleCalendarContext.Provider>
  );
}
